/**
 * Structured time horizon helpers.
 *
 * A time horizon is either:
 * - A legacy string (e.g. "Q2 2026") — displayed as-is
 * - A structured object: { type: "monthly"|"quarterly"|"yearly", year, month?, quarter? }
 */

const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

/**
 * Format a time horizon for display.
 * @param {string|object|null} th
 * @returns {string}
 */
export function formatTimeHorizon(th) {
  if (!th) return ''
  // Auto-parse if still a string (JSON or DynamoDB Map toString)
  if (typeof th === 'string') {
    const parsed = parseTimeHorizon(th)
    if (parsed && typeof parsed === 'object') return formatTimeHorizon(parsed)
    return th
  }
  if (th.type === 'monthly' && th.month && th.year) {
    return `${MONTH_NAMES[th.month]} ${th.year}`
  }
  if (th.type === 'quarterly' && th.quarter && th.year) {
    return `Q${th.quarter} ${th.year}`
  }
  if (th.type === 'yearly' && th.year) {
    return `${th.year}`
  }
  return ''
}

/**
 * Get the end date of a time horizon period.
 * @param {object} th - structured time horizon
 * @returns {Date|null}
 */
export function periodEndDate(th) {
  if (!th || typeof th === 'string') return null
  const { type, year, month, quarter } = th
  if (type === 'monthly' && month && year) {
    // Last day of the month
    return new Date(year, month, 0) // month is 1-indexed, Date(year, month, 0) = last day of previous month = last day of month
  }
  if (type === 'quarterly' && quarter && year) {
    const endMonth = quarter * 3
    return new Date(year, endMonth, 0)
  }
  if (type === 'yearly' && year) {
    return new Date(year, 12, 0) // Dec 31
  }
  return null
}

/**
 * Check if a goal's time horizon period has fully elapsed.
 * @param {object} goal
 * @returns {boolean}
 */
export function isHistorical(goal) {
  let th = goal?.timeHorizon
  if (!th) return false
  if (typeof th === 'string') {
    th = parseTimeHorizon(th)
    if (!th || typeof th === 'string') return false
  }
  const end = periodEndDate(th)
  if (!end) return false
  return end < new Date()
}

/**
 * Get the valid child time horizon options for a sub-goal given its parent's horizon.
 * @param {object} parentTh - parent goal's structured time horizon
 * @returns {Array<{value: object, label: string}>}
 */
export function childHorizonOptions(parentTh) {
  if (!parentTh) return []
  if (typeof parentTh === 'string') {
    const parsed = parseTimeHorizon(parentTh)
    if (parsed && typeof parsed === 'object') return childHorizonOptions(parsed)
    return []
  }

  const options = []

  if (parentTh.type === 'yearly') {
    const y = parentTh.year
    for (let q = 1; q <= 4; q++) {
      options.push({ value: { type: 'quarterly', year: y, quarter: q }, label: `Q${q}` })
    }
    for (let m = 1; m <= 12; m++) {
      options.push({ value: { type: 'monthly', year: y, month: m }, label: MONTH_NAMES[m] })
    }
  } else if (parentTh.type === 'quarterly') {
    const y = parentTh.year
    const q = parentTh.quarter
    options.push({ value: { type: 'quarterly', year: y, quarter: q }, label: `Q${q} ${y}` })
    const startMonth = (q - 1) * 3 + 1
    for (let m = startMonth; m < startMonth + 3; m++) {
      options.push({ value: { type: 'monthly', year: y, month: m }, label: `${MONTH_NAMES[m]} ${y}` })
    }
  } else if (parentTh.type === 'monthly') {
    options.push({ value: parentTh, label: formatTimeHorizon(parentTh) })
  }

  return options
}

/**
 * Parse a time horizon value from AWSJSON (may be a JSON string).
 * @param {*} value
 * @returns {string|object|null}
 */
export function parseTimeHorizon(value) {
  if (!value) return null
  if (typeof value === 'object') return value
  if (typeof value === 'string') {
    // Unwrap multiple JSON encoding layers (AppSync String field double-encodes)
    let current = value
    for (let i = 0; i < 3; i++) {
      try {
        const parsed = JSON.parse(current)
        if (typeof parsed === 'object' && parsed !== null) return parsed
        if (typeof parsed === 'string') { current = parsed; continue }
        break
      } catch {
        break
      }
    }
    // Try DynamoDB Map toString format: {type=yearly, year=2026, quarter=3}
    if (value.startsWith('{') && value.includes('=')) {
      try {
        const obj = {}
        const inner = value.slice(1, -1) // remove { }
        for (const pair of inner.split(', ')) {
          const [k, v] = pair.split('=')
          if (k && v !== undefined) {
            const trimK = k.trim()
            const trimV = v.trim()
            // Parse numbers
            obj[trimK] = /^\d+$/.test(trimV) ? Number(trimV) : trimV
          }
        }
        if (obj.type && obj.year) return obj
      } catch {
        // Fall through
      }
    }
    return value
  }
  return null
}
